import { Link as MuiLink } from '@mui/material';
import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';

interface LinkComponentProps {
  to: string;
  toParams?: any;
  children: React.ReactNode;
  onClick?: (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent> | undefined
  ) => void;
  disabled?: boolean;
  target?: string;
  rel?: string;
}

const LinkComponent = ({
  to,
  toParams,
  children,
  onClick,
  disabled,
  target,
  rel,
}: LinkComponentProps) => {
  const history = useHistory();

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      if (disabled) {
        event.preventDefault();
        return;
      }

      // If target is _blank, let the default behavior handle it
      if (target === '_blank') {
        if (onClick) {
          onClick(event);
        }
        return;
      }

      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const isCtrlOrCmdPressed = isMac ? event.metaKey : event.ctrlKey;

      // Open in new tab if Ctrl+Click (Windows) or Cmd+Click (Mac)
      if (isCtrlOrCmdPressed) {
        return; // Do nothing, let the browser handle the new tab opening
      }

      // Otherwise, prevent default behavior and navigate within the same tab
      event.preventDefault();
      if (toParams) {
        history.push({
          pathname: to,
          state: toParams,
        });
      } else {
        history.push(to);
      }

      // Call the onClick handler if provided
      if (onClick) {
        onClick(event);
      }
    },
    [to, toParams, onClick, disabled, target, history]
  );

  return (
    <MuiLink
      href={disabled ? undefined : to}
      onClick={handleClick}
      target={target}
      rel={rel}
      style={{ fontWeight: 400 }}
      sx={{
        textDecoration: 'none',
        fontSize: 16,
        fontFamily: 'Calibre-Regular, sans-serif',
        cursor: 'pointer',
        '&:hover': {
          textDecoration: disabled ? 'none' : 'underline',
        },
      }}
    >
      {children}
    </MuiLink>
  );
};

export default LinkComponent;
